import React, { useState, useEffect, useRef } from "react";
import "./About.css";
import "./common.css";
import map_tourist from "../assets/images/map_tourist.png";
import axios from "axios";
import { useNavigate } from "react-router-dom";




const placeholderImg =
  "https://via.placeholder.com/300x200/003366/FFFFFF?text=Coming+Soon";

const About = () => {
  const [aboutData, setAboutData] = useState({ title: "", description: "" });
  const [options, setOptions] = useState([]);
  const [upcomingTours, setUpcomingTours] = useState({});
  const [popularDestinations, setPopularDestinations] = useState({});
  const [upcomingTab, setUpcomingTab] = useState("");
  const [popularTab, setPopularTab] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(5);
  const [whatsappLink, setWhatsappLink] = useState("");
  const carouselRef = useRef(null);
  const navigate = useNavigate();

  // --- Fetch About Section ---
  useEffect(() => {
    axios
      .get("https://admin.newalliedtour.net/api/about_section/")
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data[0] : res.data;
        setAboutData({
          title: data?.title || "About Us",
          description: data?.description || "",
        });
      })
      .catch(() => setAboutData({ title: "Failed to load", description: "" }));
  }, []);

  useEffect(() => {
    axios
      .get("https://admin.newalliedtour.net/api/admin_contact_section/")
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data[0] : res.data;
        setWhatsappLink(data?.whatsapp_link || "");
      })
      .catch((err) => {
        console.error("Failed to fetch contact info:", err);
        setWhatsappLink(""); // fallback
      });
  }, []);

  // --- Fetch Options and Tours ---
  useEffect(() => {
    axios
      .get("https://admin.newalliedtour.net/api/option_section/")
      .then(async (res) => {
        const fetchedOptions = res.data;
        setOptions(fetchedOptions);

        const groupedTours = {};
        const groupedDestinations = {};
        fetchedOptions.forEach((opt) => {
          groupedTours[opt.option] = [];
          groupedDestinations[opt.option] = [];
        });

        // Upcoming Tours
        const toursRes = await axios.get(
          "https://admin.newalliedtour.net/api/upcoming_tours_section/"
        );
        toursRes.data.forEach((tour) => {
          const typeName = fetchedOptions.find((opt) => opt.id === tour.type)
            ?.option;
          if (typeName) {
            groupedTours[typeName].push({
              id: tour.id,
              name: tour.location,
              image: tour.thumbnail,
              description: tour.description,
            });
          }
        });

        // Popular Destinations
        const destRes = await axios.get(
          "https://admin.newalliedtour.net/api/popular_destination_section/"
        );
        destRes.data.forEach((dest) => {
          const typeName = fetchedOptions.find((opt) => opt.id === dest.type)
            ?.option;
          if (typeName) {
            groupedDestinations[typeName].push({
              id: dest.id,
              name: dest.location,
              image: dest.thumbnail,
            });
          }
        });

        setUpcomingTours(groupedTours);
        setPopularDestinations(groupedDestinations);

        if (fetchedOptions.length > 0) {
          setUpcomingTab(fetchedOptions[0].option);
          setPopularTab(fetchedOptions[0].option);
        }
      })
      .catch((err) => console.error("Option fetch error:", err));
  }, []);

  // Carousel auto-scroll
  useEffect(() => {
    const interval = setInterval(() => {
      const total = (popularDestinations[popularTab] || []).length;
      if (total > 0) setCurrentIndex((prev) => (prev + 1) % total);
    }, 3000);
    return () => clearInterval(interval);
  }, [popularTab, popularDestinations]);

  // Responsive visible card count for carousel
  useEffect(() => {
    const calcVisibleCount = () => {
      const w = window.innerWidth;
      if (w <= 576) return 1; // small mobile
      if (w <= 768) return 2; // mobile / small tablet
      if (w <= 992) return 3; // tablet
      return 5; // desktop
    };

    const updateVisible = () => setVisibleCount(calcVisibleCount());
    updateVisible();
    window.addEventListener("resize", updateVisible);
    return () => window.removeEventListener("resize", updateVisible);
  }, []);

  // Navigate to TourDetail and scroll to top
  const handleViewDetails = (id) => {
    navigate(`/tour/${id}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="about-container" id="About">
      <div className="about-section">
        {/* About Section */}
        <div className="about-content">
          <div className="about-text">
            <h2>{aboutData.title}</h2>
            <p dangerouslySetInnerHTML={{ __html: aboutData.description }} />
          </div>
          <div className="about-image">
            <img src={map_tourist} alt="Map of Tourist Destinations" />
          </div>
        </div>

        {/* Upcoming Tours */}
        <section>
          <h2 className="section-title">UPCOMING TOURS</h2>
          <div className="tab-container">
            <div className="tab-options-scroll">
              <div className="tab-options">
                {options.map((opt) => (
                  <div
                    key={opt.id}
                    className={`tab-option ${upcomingTab === opt.option ? "active" : ""
                      }`}
                    onClick={() => setUpcomingTab(opt.option)}
                  >
                    {opt.option.toUpperCase()}
                  </div>
                ))}
              </div>
            </div>
            <div className="tour-cards">
              {(upcomingTours[upcomingTab] || []).map((tour) => (
                <div className="tour-card" key={`${upcomingTab}-${tour.id}`}>
                  <div className="card-image">
                    <img src={tour.image || placeholderImg} alt={tour.name} />
                  </div>
                  <div className="card-content">
                    <span className="tour-name">{tour.name}</span>
                    <button
                      type="button"
                      className="view-details-btn"
                      onClick={() => handleViewDetails(tour.id)}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Popular Destinations */}
        <section id="destination">
          <h2 className="section-title">POPULAR DESTINATIONS</h2>
          <div className="tab-container">
            <div className="tab-options-scroll">
              <div className="tab-options">
                {options.map((opt) => (
                  <div
                    key={opt.id}
                    className={`tab-option ${popularTab === opt.option ? "active" : ""
                      }`}
                    onClick={() => {
                      setPopularTab(opt.option);
                      setCurrentIndex(0);
                    }}
                  >
                    {opt.option.toUpperCase()}
                  </div>
                ))}
              </div>
            </div>
            <div className="destinations-carousel-container">
              <div
                className="carousel-arrow left"
                onClick={() => {
                  const total = (popularDestinations[popularTab] || []).length;
                  setCurrentIndex((prev) => (prev - 1 + total) % total);
                }}
              >
                &#10094;
              </div>

              <div className="destinations-carousel" ref={carouselRef}>
                {(() => {
                  const destinations = popularDestinations[popularTab] || [];
                  const total = destinations.length;
                  if (total === 0) return null;

                  // Calculate indices to show (centered around currentIndex)
                  const halfVisible = Math.floor(visibleCount / 2);
                  const visibleCards = [];

                  for (let i = 0; i < visibleCount; i++) {
                    // Calculate the actual index in the array (with wrapping for infinite loop)
                    const offset = i - halfVisible;
                    const actualIndex = ((currentIndex + offset) % total + total) % total;
                    const destination = destinations[actualIndex];
                    const isCenter = i === halfVisible;

                    visibleCards.push(
                      <div
                        key={`${popularTab}-${actualIndex}-${i}`}
                        className={`destination-card ${isCenter ? "active" : ""}`}
                        style={{
                          transform: isCenter ? "scale(1.15)" : "scale(0.9)",
                          zIndex: isCenter ? 2 : 1,
                          opacity: isCenter ? 1 : 0.7,
                        }}
                      >
                        <div className="card-image">
                          <img
                            src={destination.image || placeholderImg}
                            alt={destination.name}
                          />
                        </div>
                        <div className="card-content">
                          <h3>{destination.name}</h3>
                        </div>
                      </div>
                    );
                  }

                  return visibleCards;
                })()}
              </div>

              <div
                className="carousel-arrow right"
                onClick={() => {
                  const total = (popularDestinations[popularTab] || []).length;
                  setCurrentIndex((prev) => (prev + 1) % total);
                }}
              >
                &#10095;
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* WhatsApp Floating Button */}
      <a
        href={whatsappLink}
        className="whatsapp-float"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img src={require("../assets/icons/whatsapp.png")} alt="Chat on WhatsApp" />
      </a>
    </div>
  );
};

export default About;
